import React, { useMemo, useState, useEffect } from 'react';
import { getCategoryLabel, getIssueCategory } from '../../data/analyzerRules';
import RuleTooltip from './RuleTooltip';

const SORT_KEYS = {
  severity: 'errorSeverity',
  code: 'errorCode',
  file: 'filePath',
  item: 'item',
  description: 'description'
};

const severityRank = { Error: 3, Warning: 2, Info: 1 };

const formatSeverity = (severity) => {
  if (severity === 'Error') return 'Error';
  if (severity === 'Warning') return 'Warning';
  return 'Info';
};

const AnalyzerIssueTable = ({ issues, groupBy, selectedIssueId, onSelectIssue, onSortChange, sortConfig }) => {
  const [collapsedGroups, setCollapsedGroups] = useState({});

  useEffect(() => {
    setCollapsedGroups({});
  }, [groupBy]);

  const sortedIssues = useMemo(() => {
    if (!sortConfig.key) return issues;

    const sorted = [...issues].sort((a, b) => {
      const key = sortConfig.key;
      if (key === 'errorSeverity') {
        return (severityRank[a.errorSeverity] || 0) - (severityRank[b.errorSeverity] || 0);
      }

      const aValue = (a[key] || '').toString().toLowerCase();
      const bValue = (b[key] || '').toString().toLowerCase();
      if (aValue < bValue) return -1;
      if (aValue > bValue) return 1;
      return 0;
    });

    return sortConfig.direction === 'asc' ? sorted : sorted.reverse();
  }, [issues, sortConfig]);

  const groupedIssues = useMemo(() => {
    if (groupBy === 'none') {
      return [{ groupKey: 'all', label: null, items: sortedIssues }];
    }

    const map = new Map();
    sortedIssues.forEach((issue) => {
      let key = '';
      let label = '';
      if (groupBy === 'severity') {
        key = issue.errorSeverity || 'Info';
        label = formatSeverity(key);
      } else if (groupBy === 'category') {
        key = getIssueCategory(issue);
        label = getCategoryLabel(issue.errorCode);
      } else if (groupBy === 'file') {
        key = issue.filePath || 'Unknown file';
        label = issue.filePath || 'Unknown file';
      }

      if (!map.has(key)) {
        map.set(key, { groupKey: key, label, items: [] });
      }
      map.get(key).items.push(issue);
    });

    return Array.from(map.values());
  }, [sortedIssues, groupBy]);

  const toggleGroup = (groupKey) => {
    setCollapsedGroups((prev) => ({
      ...prev,
      [groupKey]: !prev[groupKey]
    }));
  };

  const renderHeader = (label, key) => (
    <button
      type="button"
      onClick={() => onSortChange(key)}
      className="flex items-center gap-1 text-left"
    >
      <span>{label}</span>
      {sortConfig.key === key && (
        <span className="text-xs" style={{color:'rgba(255,255,255,0.35)'}}>{sortConfig.direction === 'asc' ? '▲' : '▼'}</span>
      )}
    </button>
  );

  return (
    <div className="rounded-xl overflow-hidden" style={{border:'1px solid var(--tqa-border-dark)',background:'var(--tqa-surface)'}}>
      <div className="grid grid-cols-12 gap-3 px-4 py-2 text-xs font-semibold uppercase tracking-wide" style={{color:'rgba(255,255,255,0.45)',borderBottom:'1px solid var(--tqa-border-dark)',background:'var(--tqa-surface-2)'}}>
        <div className="col-span-2">{renderHeader('Severity', SORT_KEYS.severity)}</div>
        <div className="col-span-2">{renderHeader('Code', SORT_KEYS.code)}</div>
        <div className="col-span-2">{renderHeader('File', SORT_KEYS.file)}</div>
        <div className="col-span-2">{renderHeader('Item', SORT_KEYS.item)}</div>
        <div className="col-span-4">{renderHeader('Description', SORT_KEYS.description)}</div>
      </div>

      {groupedIssues.map((group) => (
        <div key={group.groupKey || 'all'}>
          {group.label && (
            <button
              type="button"
              onClick={() => toggleGroup(group.groupKey)}
              className="w-full flex items-center justify-between px-4 py-2 text-sm font-semibold" style={{background:'var(--tqa-surface-2)',color:'rgba(255,255,255,0.8)'}}
            >
              <span>{group.label}</span>
              <span className="text-xs" style={{color:'rgba(255,255,255,0.45)'}}>
                {group.items.length} issue{group.items.length !== 1 ? 's' : ''}
                <span className="ml-2">{collapsedGroups[group.groupKey] ? '▸' : '▾'}</span>
              </span>
            </button>
          )}

          {!collapsedGroups[group.groupKey] && group.items.map((issue) => (
            <button
              type="button"
              key={issue.id}
              onClick={() => onSelectIssue(issue)}
              className={`w-full text-left grid grid-cols-12 gap-3 px-4 py-2 text-sm ${
                selectedIssueId === issue.id ? 'bg-white/8' : 'hover:bg-white/5'
              }`}
              style={{borderBottom:'1px solid rgba(255,255,255,0.06)'}}
            >
              <div className="col-span-2" style={{color:'rgba(255,255,255,0.75)'}}>
                <span className="inline-flex items-center gap-2">
                  <span
                    className={`h-2 w-2 rounded-full ${
                      issue.errorSeverity === 'Error'
                        ? 'bg-rose-500'
                        : issue.errorSeverity === 'Warning'
                          ? 'bg-amber-400'
                          : 'bg-sky-500'
                    }`}
                  ></span>
                  {formatSeverity(issue.errorSeverity)}
                </span>
              </div>
              <div className="col-span-2 font-mono text-xs" style={{color:'rgba(255,255,255,0.75)'}}>
                <RuleTooltip issue={issue}>
                  <span>{issue.errorCode || 'Unknown'}</span>
                </RuleTooltip>
              </div>
              <div className="col-span-2 truncate" style={{color:'rgba(255,255,255,0.55)'}}>
                {issue.filePath || 'Unknown'}
              </div>
              <div className="col-span-2 truncate" style={{color:'rgba(255,255,255,0.55)'}}>
                {issue.item || '-'}
              </div>
              <div className="col-span-4 truncate" style={{color:'rgba(255,255,255,0.55)'}}>
                {issue.description || 'No description available'}
              </div>
            </button>
          ))}
        </div>
      ))}

      {issues.length === 0 && (
        <div className="px-4 py-10 text-center text-sm" style={{color:'rgba(255,255,255,0.4)'}}>
          No issues match the current filters.
        </div>
      )}
    </div>
  );
};

export default AnalyzerIssueTable;
